import { FC, ReactNode, useCallback } from 'react';
import { useForm, FormProvider, FormState, Controller } from 'react-hook-form';
import { Grid, Stack, TextField } from '@mui/material';

import { CharacterCount } from '@shared/components/CharacterCount';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { WordCount } from '@shared/components/WordCount';
import { StoryAnalysisLoader } from '@modules/Onboarding/components/OnboardingWizard/StoryAnalysisLoader';
import { LengthEstimates } from '@shared/components/LengthEstimates';
import { MAX_STORY_CONTENT_LENGTH } from '@cvt/helpers/validation';


export type FormData = {
  story?: string;
};

interface Props {
    onSubmitRequest: (values: FormData) => void;
    onFieldChange?: (fieldName: string, value: any) => void;
    slots?: {
      footer?: ReactNode | ((formState: FormState<FormData>) => ReactNode);
    }
    defaultValues?: Partial<FormData>;
};

const DEFAULT_VALUES: Partial<FormData> = {
  story: '',
};


export const StoryForm: FC<Props> = (props) => {
  const { onSubmitRequest, slots, defaultValues = DEFAULT_VALUES, onFieldChange } = props;
  const dictionary = useDictionary();

  const methods = useForm<FormData>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { handleSubmit, formState } = methods;


  const onSubmit = useCallback(async (data: FormData) => {
    const payload = {
      ...data,
    };

    return onSubmitRequest(payload);
  }, [onSubmitRequest]);

  return (
    <FormProvider {...methods}>
      <form style={{ width: '100%', height: '100%' }} onSubmit={handleSubmit(onSubmit)}>
        <Stack justifyContent="space-between" height="100%" gap={4}>
          <Grid container spacing={3} mt={2}>
            <Grid size={12}>
              <Controller
                name="story"
                rules={{
                  required: dictionary.forms.validations.required,
                  maxLength: {
                    value: MAX_STORY_CONTENT_LENGTH,
                    message: dictionary.forms.validations.maxLength(MAX_STORY_CONTENT_LENGTH),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    multiline
                    minRows={5}
                    error={!!fieldState.error}
                    helperText={(
                      <Stack component="span">
                        <span>{fieldState.error?.message}</span>
                        <Stack component="span" direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                          <Stack component="span" direction="row" gap={2} alignItems="center">
                            <WordCount text={field.value} fontWeight={500} display={{ xs: 'none', sm: 'inline' }}/>
                            <LengthEstimates length={field.value.length}/>
                          </Stack>

                          <Stack component="span" direction="row" justifyContent="space-between" alignItems="center" width={{ xs: '100%', sm: 'auto' }} gap={2}>
                            <WordCount text={field.value} fontWeight={500} display={{ xs: 'inline', sm: 'none' }}/>
                            <CharacterCount
                              text={field.value}
                              maxLength={MAX_STORY_CONTENT_LENGTH}
                              flexShrink={0}
                              mediumCount={120}
                              preferredLength={150}
                              fontWeight={500}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    )}
                    placeholder={dictionary.forms.onboarding.story.placeholder}
                    onChange={(event) => {
                      field.onChange(event);
                      onFieldChange?.('story', event.target.value);
                    }}
                  />
                )}
              />

            </Grid>

            {formState.isSubmitting && (
              <Grid size={12}>
                <StoryAnalysisLoader/>
              </Grid>
            )}

            <Grid size={12}>
              {typeof slots?.footer === 'function' ? slots?.footer(formState) : slots?.footer}
            </Grid>
          </Grid>

        </Stack>
      </form>
    </FormProvider>
  );
};
